/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo } from "react";
import DetailComponent from "./DetailComponent";
import { FilterColor } from "../../svgs";
import axios from "axios";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { MdChevronRight } from "react-icons/md";
import Map from "../map/map";
import SliderDetailVenueTab from "./SliderDetailVenueTab";
import { base_URL } from "../../config/config";
import { useLocation } from "react-router-dom";
import { deepClean } from "../../utils/utilityFunctions";
import BulkEnquiry from "./BulkEnquiry";
import Modal from "../shared/Modal";
import BulkEnquiryModal from "../myFav/BulkEnquiryModal";
import {
	closeBulkEnquiry,
	openBulkEnquiry,
	setBulkEnquiryModal,
} from "../../store/features/bulkEnquirySlice";
import { useDispatch, useSelector } from "react-redux";

const fetchRestaurants = async (page, sort, filters, signal) => {
	const { data } = await axios.get(`${base_URL}/api/venues/get-all`, {
		params: { page, limit: 24, sort, ...deepClean(filters || {}) },
		signal,
	});
	return {
		total: data.total,
		venues: data.venues,
		totalPages: data.totalPages || 0,
	};
};

const CardSkeleton = () => (
	<div className="rounded-xl animate-pulse bg-gray-100 h-[480px]" />
);

const sortOptions = [
	{ name: "Price Per Person", options: ["Low to High", "High to Low"] },
	{ name: "Minimum Spend", options: ["Low to High", "High to Low"] },
	{ name: "Offers Availability" },
	{ name: "Newest" },
	{ name: "Popularity (Most Viewed)" },
];

const CategoryCarousel = ({
	filters,
	setFilters,
	isShowMap,
	setIsShowMap,
	city,
	setCount,
	count,
	breadcrumbPath = [],
	page,
	setPage,
}) => {
	const [isMobileView, setIsMobileView] = useState(false);
	const [filterVenues, setFilterVenues] = useState([]);
	const [mapBounds, setMapBounds] = useState(null);
	const [venues, setVenues] = useState([]);
	const [showSort, setShowSort] = useState(false);
	const [sortType, setSortType] = useState("default");
	const [sortSubType, setSortSubType] = useState("default");
	const [sortValue, setSortValue] = useState("");
	const [totalCount, setTotalCount] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const location = useLocation();
	const pageRequestedRef = useRef(1);
	const loadMoreRef = useRef(null);

	const bulkEnquiry = useSelector((state) => state.bulkEnquiry);
	const dispatch = useDispatch();

	const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

	const { data, isLoading, error, isFetching, isPlaceholderData } = useQuery({
		queryKey: ["venuesAndRestaurants", sortValue, filterKey, page],
		queryFn: ({ signal }) => fetchRestaurants(page, sortValue, filters, signal),
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		dispatch(closeBulkEnquiry());
	}, []);

	useEffect(() => {
		const state = location.state;
		if (state && state.filters) setFilterVenues(state.filters);
	}, []);

	useEffect(() => {
		pageRequestedRef.current = 1;
		setPage(1);
		setVenues([]);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [filterKey]);

	// handle data updates — skip placeholder (stale previous page) data
	useEffect(() => {
		if (!data || isPlaceholderData) return;
		setTotalCount(data.total);
		setTotalPages(data.totalPages || 0);
		setCount(data.total);
		pageRequestedRef.current = page;

		setVenues((prev) => {
			if (page === 1) return data.venues;
			const merged = [...prev, ...data.venues];
			return merged.filter(
				(v, i, self) => i === self.findIndex((x) => x._id === v._id)
			);
		});
	}, [data, page, isPlaceholderData]);

	useEffect(() => {
		if (!mapBounds || venues.length === 0) return;
		const visibleVenues = venues.filter((v) => {
			const { lat, lng } = v.location;
			return (
				lat >= mapBounds.south &&
				lat <= mapBounds.north &&
				lng >= mapBounds.west &&
				lng <= mapBounds.east
			);
		});
		setFilterVenues(visibleVenues);
	}, [mapBounds, venues]);

	useEffect(() => {
		const handleResize = () => setIsMobileView(window.innerWidth < 1024);
		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Infinite scroll: load next page when sentinel enters viewport
	useEffect(() => {
		const el = loadMoreRef.current;
		if (!el) return;
		const obs = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !isFetching && page < totalPages && pageRequestedRef.current === page) {
					pageRequestedRef.current = page + 1;
					setPage((prev) => prev + 1);
				}
			},
			{ rootMargin: "300px" }
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, [isFetching, page, totalPages]);

	const handleSort = (type) => {
		setSortType(type);
		if (
			type === "Offers Availability" ||
			type === "Newest" ||
			type === "Popularity (Most Viewed)"
		) {
			setSortValue(
				type === "Offers Availability"
					? "Offers"
					: type === "Newest"
					? "Newest"
					: "Popular"
			);
			setShowSort(false);
		} else if (type === "Price Per Person") {
			setSortSubType("pricePerPerson");
		} else if (type === "Minimum Spend") {
			setSortSubType("minimumSpend");
		}
	};

	const handleSubSort = (sub) => {
		if (sortType === "Price Per Person") {
			setSortValue(sub === "Low to High" ? "PersonLow" : "PersonHigh");
		} else if (sortType === "Minimum Spend") {
			setSortValue(sub === "Low to High" ? "SpendLow" : "SpendHigh");
		}
		setShowSort(false);
	};

	if (isLoading && venues.length === 0)
		return (
			<div className="grid w-full lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-6">
				{Array.from({ length: 6 }).map((_, i) => (
					<CardSkeleton key={i} />
				))}
			</div>
		);
	if (error) return <p>Error loading venues</p>;

	return (
		<div className="relative w-full">
			<div className="flex flex-col gap-4">
				{/* Header */}
				<div className="flex w-full justify-between font-medium items-center">
					<h1 className="text-lg">
						{isShowMap && filterVenues.length
							? `${filterVenues.length} `
							: `${totalCount || 0} `}
						venues in {filters.city || "All Cities"}
					</h1>

					{/* Sort */}
					<div className="flex items-center gap-2 relative">
						<button
							className="text-primary font-medium px-5 py-2 rounded-xl shadow-lg hover:shadow-lg transition-all duration-300 ease-in-out"
							onClick={() => dispatch(openBulkEnquiry())}
						>
							Bulk Enquiry
						</button>
						<div
							className="flex items-center gap-2 cursor-pointer"
							onClick={() => setShowSort((prev) => !prev)}
						>
							<p>Sort</p>
							<FilterColor />
						</div>

						{showSort && (
							<div className="absolute top-8 right-0 bg-white shadow-md z-50 w-[170px] rounded-lg p-2">
								{sortOptions.map((option, i) => (
									<div key={i} className="relative">
										<div
											className="flex justify-between items-center hover:bg-[#f5f5f5] p-2 rounded-lg cursor-pointer"
											onClick={() => handleSort(option.name)}
										>
											<p className="text-[14px] font-normal">{option.name}</p>
											{option.options && <MdChevronRight />}
										</div>
										{option.options && sortType === option.name && (
											<div className="absolute bg-white shadow-md right-[175px] w-[160px] p-2 rounded-lg z-50">
												{option.options.map((sub, j) => (
													<p
														key={j}
														className="text-[14px] p-2 hover:bg-[#f5f5f5] rounded-lg cursor-pointer"
														onClick={() => handleSubSort(sub)}
													>
														{sub}
													</p>
												))}
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				</div>

				{isShowMap ? (
					<div className="grid w-full lg:grid-cols-3 gap-6">
						<div className="hidden lg:grid grid-cols-1 gap-6 overflow-y-auto">
							{filterVenues.map((v, i) => (
								<DetailComponent key={v._id || i} data={v} priority={i < 6} />
							))}
						</div>
						<div className="lg:col-span-2 relative">
							<Map
								city={filters.city || city}
								setIsShowMap={setIsShowMap}
								places={venues}
								setMapBounds={setMapBounds}
								setFilterVenues={setFilterVenues}
							/>
							{isMobileView && filterVenues.length > 0 && (
								<div className="absolute top-[54vh] w-full overflow-hidden">
									<SliderDetailVenueTab data={filterVenues} />
								</div>
							)}
						</div>
					</div>
				) : (
					<div className="w-full flex flex-col gap-4">
						<div className="grid w-full lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-6">
							{venues.map((venue, i) => (
								<DetailComponent key={venue._id || i} data={venue} priority={i < 6} breadcrumbPath={breadcrumbPath} />
							))}
						</div>
						{/* Sentinel — IntersectionObserver watches this to trigger next page load */}
						<div ref={loadMoreRef} className="h-4" />
						{isFetching && (
							<div className="flex justify-center py-3">
								<div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
							</div>
						)}

						<BulkEnquiry />
						{bulkEnquiry?.isBulkEnquiryModal && (
							<Modal
								isOpen={bulkEnquiry?.isBulkEnquiryModal}
								onClose={() => dispatch(setBulkEnquiryModal(false))}
							>
								<BulkEnquiryModal
									onClose={() => dispatch(setBulkEnquiryModal(false))}
									selectedEnquiries={bulkEnquiry?.selectedVenues || []}
									enquiryType={bulkEnquiry?.enquiryType || "venue"}
									isRedirection={false}
								/>
							</Modal>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default CategoryCarousel;
